import Menu from './menu';

import Util from '../utils/util';

import { Modules, Opcodes } from '@kaetram/common/network';

import type ItemInfo from './iteminfo';
import type Inventory from './inventory';
import type { PlayerShopItem, PlayerShopPacket } from '@kaetram/common/types/messages/outgoing';

type SendCallback = (opcode: Opcodes.PlayerShop, data: object) => void;

export default class PlayerShopMenu extends Menu {
    private static SHOP_SLOTS = 12;

    private setupDiv: HTMLElement = document.querySelector('#player-shop-setup')!;
    private browseDiv: HTMLElement = document.querySelector('#player-shop-browse')!;

    private inventoryList: HTMLUListElement = document.querySelector(
        '#player-shop-inventory-slots > ul'
    )!;
    private shopSlotsList: HTMLUListElement = document.querySelector('#player-shop-slots > ul')!;
    private browseSlotsList: HTMLUListElement = document.querySelector(
        '#player-shop-browse-slots > ul'
    )!;

    private sellerName: HTMLElement = document.querySelector('#player-shop-seller-name')!;
    private openBtn: HTMLElement = document.querySelector('#player-shop-open-btn')!;
    private closeBtn: HTMLElement = document.querySelector('#player-shop-close-btn')!;

    private priceDialog: HTMLDialogElement = document.querySelector('#player-shop-price-dialog')!;
    private priceInput: HTMLInputElement = document.querySelector('#player-shop-price')!;
    private currencySelect: HTMLSelectElement = document.querySelector('#player-shop-currency')!;
    private priceItemImage: HTMLElement;
    private priceItemName: HTMLElement;
    private priceAccept: HTMLElement;
    private priceCancel: HTMLElement;

    private buyDialog: HTMLDialogElement = document.querySelector('#player-shop-buy-dialog')!;
    private buyInput: HTMLInputElement;
    private buyAccept: HTMLElement;
    private buyCancel: HTMLElement;

    private browseInfo: HTMLElement | undefined;

    private pendingInventoryIndex = -1;
    private browsingInstance = '';
    private browsingBuyIndex = -1;
    private selectedBrowseIndex = -1;
    private isOpen = false;

    private listings: PlayerShopItem[] = [];

    public sendCallback?: SendCallback;

    public constructor(private inventory: Inventory, private itemInfo?: ItemInfo) {
        super('#player-shop', '#close-player-shop');

        this.priceItemImage = this.priceDialog.querySelector('.dialog-item-image')!;
        this.priceItemName = this.priceDialog.querySelector('.dialog-item-name')!;
        this.priceAccept = this.priceDialog.querySelector('.dialog-accept')!;
        this.priceCancel = this.priceDialog.querySelector('.dialog-cancel')!;

        this.buyInput = this.buyDialog.querySelector('.dialog-count')!;
        this.buyAccept = this.buyDialog.querySelector('.dialog-accept')!;
        this.buyCancel = this.buyDialog.querySelector('.dialog-cancel')!;

        this.openBtn.addEventListener('click', () => this.handleOpen());
        this.closeBtn.addEventListener('click', () => this.handleClose());

        this.priceAccept.addEventListener('click', () => this.confirmSetItem());
        this.priceCancel.addEventListener('click', () => this.hidePriceDialog());

        this.buyAccept.addEventListener('click', () => this.confirmBuy());
        this.buyCancel.addEventListener('click', () => this.buyDialog.close());

        this.load();
    }

    private load(): void {
        for (let i = 0; i < Modules.Constants.INVENTORY_SIZE; i++)
            this.inventoryList.append(
                Util.createSlot(Modules.ContainerType.Inventory, i, (_type, index) =>
                    this.handleInventoryClick(index)
                )
            );

        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let li = document.createElement('li'),
                slot = document.createElement('div'),
                image = document.createElement('div'),
                count = document.createElement('div');

            slot.classList.add('item-slot', 'empty-commerce');
            slot.dataset.index = `${i}`;
            image.classList.add('item-image');
            count.classList.add('item-count');

            slot.append(image, count);
            slot.addEventListener('click', () => this.handleShopSlotClick(i));

            li.append(slot);
            this.shopSlotsList.append(li);
        }

        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let li = document.createElement('li'),
                slot = document.createElement('div'),
                image = document.createElement('div'),
                count = document.createElement('div');

            slot.classList.add('item-slot', 'empty-commerce');
            slot.dataset.index = `${i}`;
            image.classList.add('item-image');
            count.classList.add('item-count');

            slot.append(image, count);
            slot.addEventListener('click', () => this.handleBrowseSlotClick(i));
            slot.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.showItemInfo(i, e.clientX, e.clientY);
            });

            li.append(slot);
            this.browseSlotsList.append(li);
        }
    }

    public override synchronize(): void {
        if (!this.isVisible()) return;

        this.inventory.forEachSlot((index: number, slot: HTMLElement) => {
            let target =
                    this.inventoryList.children[index]?.querySelector<HTMLElement>('.item-slot'),
                image = target?.querySelector<HTMLElement>('.item-image'),
                countEl = target?.querySelector<HTMLElement>('.item-count'),
                slotImage = slot.querySelector<HTMLElement>('.item-image');

            if (!image || !countEl || !slotImage) return;

            image.style.backgroundImage = slotImage.style.backgroundImage;
            countEl.textContent = slot.querySelector<HTMLElement>('.item-count')?.textContent || '';
        });
    }

    public showSetup(): void {
        this.setupDiv.hidden = false;
        this.browseDiv.hidden = true;
        this.isOpen = false;

        this.openBtn.hidden = false;
        this.closeBtn.hidden = true;

        this.listings = [];
        this.clearShopSlots();

        Util.fadeIn(this.container);
        this.synchronize();
    }

    public showShopActive(): void {
        this.setupDiv.hidden = false;
        this.browseDiv.hidden = true;
        this.openBtn.hidden = true;
        this.closeBtn.hidden = false;
        this.isOpen = true;

        Util.fadeIn(this.container);
        this.synchronize();
    }

    public showBrowse(data: PlayerShopPacket): void {
        this.setupDiv.hidden = true;
        this.browseDiv.hidden = false;
        this.browsingInstance = data.instance || '';
        this.sellerName.textContent = `Tienda de ${data.username || 'Desconocido'}`;

        this.renderBrowseSlots(data.items || []);

        Util.fadeIn(this.container);
    }

    public updateListings(items: PlayerShopItem[]): void {
        this.listings = items;
        this.renderShopSlots();
    }

    private renderShopSlots(): void {
        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let slot = this.shopSlotsList.children[i]?.querySelector<HTMLElement>('.item-slot');

            if (!slot) continue;

            let image = slot.querySelector<HTMLElement>('.item-image')!,
                count = slot.querySelector<HTMLElement>('.item-count')!;

            slot.querySelector('.shop-slot-price')?.remove();
            slot.querySelector('.shop-slot-currency-icon')?.remove();

            let listing = this.listings[i];

            if (listing) {
                slot.classList.remove('empty-commerce');
                image.style.backgroundImage = Util.getImageURL(listing.key);
                count.textContent = listing.count > 1 ? Util.getCount(listing.count) : '';

                let priceEl = document.createElement('div');
                priceEl.classList.add('shop-slot-price');
                priceEl.textContent = Util.getCount(listing.price);
                slot.append(priceEl);

                let currIcon = document.createElement('div');
                currIcon.classList.add('shop-slot-currency-icon');
                currIcon.style.backgroundImage = Util.getImageURL(listing.currency);
                slot.append(currIcon);
            } else {
                slot.classList.add('empty-commerce');
                image.style.backgroundImage = '';
                count.textContent = '';
            }
        }
    }

    private renderBrowseSlots(items: PlayerShopItem[]): void {
        this.selectedBrowseIndex = -1;
        this.removeBrowseInfo();

        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let slot = this.browseSlotsList.children[i]?.querySelector<HTMLElement>('.item-slot');

            if (!slot) continue;

            let image = slot.querySelector<HTMLElement>('.item-image')!,
                count = slot.querySelector<HTMLElement>('.item-count')!;

            slot.querySelector('.shop-slot-price')?.remove();
            slot.querySelector('.shop-slot-currency-icon')?.remove();
            slot.classList.remove('selected');

            let item = items[i];

            if (item) {
                slot.classList.remove('empty-commerce');
                image.style.backgroundImage = Util.getImageURL(item.key);
                count.textContent = item.count > 1 ? Util.getCount(item.count) : '';

                let priceEl = document.createElement('div');
                priceEl.classList.add('shop-slot-price');
                priceEl.textContent = Util.getCount(item.price);
                slot.append(priceEl);

                let currIcon = document.createElement('div');
                currIcon.classList.add('shop-slot-currency-icon');
                currIcon.style.backgroundImage = Util.getImageURL(item.currency);
                slot.append(currIcon);
            } else {
                slot.classList.add('empty-commerce');
                image.style.backgroundImage = '';
                count.textContent = '';
            }
        }

        this.browseItems = items;
    }

    private browseItems: PlayerShopItem[] = [];

    private handleInventoryClick(index: number): void {
        if (!this.setupDiv || this.setupDiv.hidden) return;

        if (this.listings.length >= PlayerShopMenu.SHOP_SLOTS) return;

        let slot = this.inventoryList.children[index]?.querySelector<HTMLElement>('.item-slot'),
            image = slot?.querySelector<HTMLElement>('.item-image');

        if (!image?.style.backgroundImage) return;

        let slotEl = slot as HTMLElement & { dataset: { key?: string } },
            key = slotEl?.dataset?.key || '';

        this.pendingInventoryIndex = index;

        this.priceItemImage.style.backgroundImage = image.style.backgroundImage;

        let inventorySlotEl = this.getInventoryElement(index);
        this.priceItemName.textContent = inventorySlotEl?.name || key || 'Item';

        this.priceInput.value = '1';
        this.currencySelect.value = 'gold';

        this.priceDialog.show();
    }

    private getInventoryElement(index: number): HTMLElement & { name?: string } {
        let children = document.querySelector('#inventory-container > ul')?.children;

        if (!children || index >= children.length) return document.createElement('div') as any;

        return children[index]?.querySelector('.item-slot') as any;
    }

    private handleShopSlotClick(index: number): void {
        if (!this.isOpen && this.listings[index])
            this.sendCallback?.(Opcodes.PlayerShop.RemoveItem, { index });
    }

    private handleBrowseSlotClick(index: number): void {
        let item = this.browseItems[index];

        if (!item) return;

        this.selectedBrowseIndex = index;

        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let s = this.browseSlotsList.children[i]?.querySelector<HTMLElement>('.item-slot');
            s?.classList.toggle('selected', i === index);
        }

        this.showBrowseInfo(item);
    }

    private showBrowseInfo(item: PlayerShopItem): void {
        this.removeBrowseInfo();

        let info = document.createElement('div');
        info.className = 'browse-item-info';

        let name = document.createElement('span');
        name.className = 'browse-item-name';
        name.textContent = `${item.name} x${item.count}`;

        let price = document.createElement('span');
        price.className = 'browse-item-price';
        price.textContent = `${item.price} ${item.currencyName}`;

        let btn = document.createElement('button');
        btn.className = 'browse-buy-btn';
        btn.textContent = 'Comprar';
        btn.addEventListener('click', () => {
            this.browsingBuyIndex = item.index;
            this.buyInput.value = '1';
            (this.buyInput as HTMLInputElement).max = String(item.count);
            this.buyDialog.show();
        });

        info.append(name, price, btn);
        this.browseDiv.append(info);
        this.browseInfo = info;
    }

    private removeBrowseInfo(): void {
        this.browseInfo?.remove();
        this.browseInfo = undefined;
    }

    private confirmSetItem(): void {
        if (this.pendingInventoryIndex < 0) return;

        let price = parseInt(this.priceInput.value) || 1,
            currency = this.currencySelect.value;

        this.sendCallback?.(Opcodes.PlayerShop.SetItem, {
            index: this.pendingInventoryIndex,
            price,
            currency
        });

        this.pendingInventoryIndex = -1;
        this.hidePriceDialog();
    }

    private hidePriceDialog(): void {
        this.priceDialog.close();
        this.pendingInventoryIndex = -1;
    }

    private handleOpen(): void {
        this.sendCallback?.(Opcodes.PlayerShop.Open, {});
    }

    private handleClose(): void {
        this.sendCallback?.(Opcodes.PlayerShop.Close, {});
    }

    private confirmBuy(): void {
        let count = parseInt(this.buyInput.value) || 1;

        this.sendCallback?.(Opcodes.PlayerShop.Buy, {
            instance: this.browsingInstance,
            index: this.browsingBuyIndex,
            count
        });

        this.buyDialog.close();
    }

    private clearShopSlots(): void {
        for (let i = 0; i < PlayerShopMenu.SHOP_SLOTS; i++) {
            let slot = this.shopSlotsList.children[i]?.querySelector<HTMLElement>('.item-slot');

            if (!slot) continue;

            let image = slot.querySelector<HTMLElement>('.item-image')!,
                count = slot.querySelector<HTMLElement>('.item-count')!;

            slot.querySelector('.shop-slot-price')?.remove();
            slot.querySelector('.shop-slot-currency-icon')?.remove();

            slot.classList.add('empty-commerce');
            image.style.backgroundImage = '';
            count.textContent = '';
        }
    }

    public closeShop(): void {
        this.isOpen = false;
        this.hide();
    }

    public override hide(): void {
        this.removeBrowseInfo();
        this.hidePriceDialog();
        this.buyDialog.close();

        super.hide();
    }

    private showItemInfo(browseIndex: number, x: number, y: number): void {
        let item = this.browseItems[browseIndex];

        if (!item) return;

        this.itemInfo?.show(
            {
                key: item.key,
                name: item.name,
                count: item.count,
                description: item.description,
                equipmentType: item.equipmentType as Modules.Equipment | undefined,
                attackStats: item.attackStats,
                defenseStats: item.defenseStats,
                bonuses: item.bonuses,
                enchantments: item.enchantments,
                passiveAbility: item.passiveAbility,
                movementModifier: item.movementModifier
            },
            x,
            y
        );
    }

    public onSend(callback: SendCallback): void {
        this.sendCallback = callback;
    }
}
